import React from 'react';

/**
 * Where a creator gets paid: bank account, PAN, GST and MSME details.
 *
 * Shared by the creator's own payout settings and the no-sign-in exception
 * form, so both collect the same fields with the same client-side checks
 * the API applies (influencer/payout.py). File uploads are optional props:
 * the exception form deliberately collects text only.
 */

export const EMPTY_PAYOUT = {
  account_holder_name: '',
  account_number: '',
  ifsc_code: '',
  bank_name: '',
  pan_card_no: '',
  gst_number: '',
  is_msme: false,
  msme_udyam_number: '',
  msme_category: '',
};

export const PAYOUT_CSS = `
.pf-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px 16px; }
.pf-full { grid-column: 1 / -1; }
.pf-group { margin-top: 22px; padding-top: 18px; border-top: 1px solid var(--line); }
.pf-group__title { font-size: 13px; font-weight: 800; color: var(--ink); margin: 0 0 2px; }
.pf-group__sub { font-size: 12.5px; color: var(--ink-3); margin: 0 0 14px; }
.pf-toggle { display: flex; align-items: center; gap: 10px; font-size: 14px; font-weight: 700; color: var(--ink); cursor: pointer; user-select: none; }
.pf-toggle input { width: 17px; height: 17px; accent-color: var(--accent); }
.pf-file { font-size: 13px; }
@media (max-width: 560px) { .pf-grid { grid-template-columns: 1fr; } }
`;

const ACCOUNT_RE = /^\d{9,18}$/;
const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const GSTIN_RE = /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const UDYAM_RE = /^UDYAM-[A-Z]{2}-\d{2}-\d{7}$/;

const squash = (value) => String(value || '').replace(/\s/g, '').toUpperCase();

/** Client-side mirror of the API's checks. Returns `{field: message}`. */
export function validatePayout(values) {
  const errors = {};
  if (!String(values.account_holder_name || '').trim()) {
    errors.account_holder_name = 'Enter the name on the bank account.';
  }
  if (!ACCOUNT_RE.test(String(values.account_number || '').replace(/[\s-]/g, ''))) {
    errors.account_number = 'Enter the account number as 9 to 18 digits.';
  }
  if (!IFSC_RE.test(squash(values.ifsc_code))) {
    errors.ifsc_code = 'Enter a valid 11-character IFSC, e.g. HDFC0001234.';
  }
  if (!String(values.bank_name || '').trim()) {
    errors.bank_name = 'Enter the bank name.';
  }
  if (!PAN_RE.test(squash(values.pan_card_no))) {
    errors.pan_card_no = 'Enter a valid PAN, e.g. ABCDE1234F.';
  }
  if (values.gst_number && !GSTIN_RE.test(squash(values.gst_number))) {
    errors.gst_number = 'Enter a valid 15-character GSTIN.';
  }
  if (values.is_msme) {
    if (!UDYAM_RE.test(squash(values.msme_udyam_number))) {
      errors.msme_udyam_number = 'Enter the Udyam number as printed, e.g. UDYAM-MH-01-0001234.';
    }
    if (!values.msme_category) {
      errors.msme_category = 'Pick micro, small or medium.';
    }
  }
  return errors;
}

/** The payload the API expects, normalised the way it normalises. */
export function payoutPayload(values) {
  return {
    account_holder_name: String(values.account_holder_name || '').trim(),
    account_number: String(values.account_number || '').replace(/[\s-]/g, ''),
    ifsc_code: squash(values.ifsc_code),
    bank_name: String(values.bank_name || '').trim(),
    pan_card_no: squash(values.pan_card_no),
    gst_number: squash(values.gst_number),
    is_msme: values.is_msme ? 'true' : 'false',
    msme_udyam_number: values.is_msme ? squash(values.msme_udyam_number) : '',
    msme_category: values.is_msme ? values.msme_category || '' : '',
  };
}

function errorText(errors, key) {
  const value = errors && errors[key];
  if (!value) return '';
  return Array.isArray(value) ? value.join(' ') : String(value);
}

function TextField({ name, label, hint, values, errors, onChange, full, ...rest }) {
  const error = errorText(errors, name);
  return (
    <label className={`iv-field${full ? ' pf-full' : ''}`}>
      <span className="iv-label">{label}</span>
      <input
        className={`iv-input${error ? ' iv-input--invalid' : ''}`}
        value={values[name] || ''}
        onChange={(e) => onChange(name, e.target.value)}
        {...rest}
      />
      {error ? <span className="iv-hint iv-hint--error">{error}</span> : null}
      {!error && hint ? <span className="iv-hint">{hint}</span> : null}
    </label>
  );
}

export default function PayoutFields({ values, errors = {}, onChange, files, onFile, disabled }) {
  const common = { values, errors, onChange, disabled };
  return (
    <div>
      <div className="pf-grid">
        <TextField
          {...common}
          name="account_holder_name"
          label="Account holder name"
          autoComplete="name"
          full
        />
        <TextField
          {...common}
          name="account_number"
          label="Account number"
          inputMode="numeric"
          placeholder="9 to 18 digits"
        />
        <TextField
          {...common}
          name="ifsc_code"
          label="IFSC"
          placeholder="HDFC0001234"
          style={{ textTransform: 'uppercase' }}
        />
        <TextField {...common} name="bank_name" label="Bank name" full />
        <TextField
          {...common}
          name="pan_card_no"
          label="PAN"
          placeholder="ABCDE1234F"
          style={{ textTransform: 'uppercase' }}
        />
        <TextField
          {...common}
          name="gst_number"
          label="GSTIN"
          hint="Optional — only if you are GST registered."
          style={{ textTransform: 'uppercase' }}
        />
        {onFile ? (
          <label className="iv-field pf-full">
            <span className="iv-label">PAN card image</span>
            <input
              className="pf-file"
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              onChange={(e) => onFile('pan_card_image', e.target.files && e.target.files[0])}
              disabled={disabled}
            />
            <span className="iv-hint">
              {files && files.pan_card_image
                ? files.pan_card_image.name
                : 'Optional — PDF, PNG or JPG.'}
            </span>
          </label>
        ) : null}
      </div>

      <div className="pf-group">
        <p className="pf-group__title">MSME</p>
        <p className="pf-group__sub">
          Registered under Udyam? Finance needs this to meet the MSME payment timelines.
        </p>
        <label className="pf-toggle">
          <input
            type="checkbox"
            checked={Boolean(values.is_msme)}
            onChange={(e) => onChange('is_msme', e.target.checked)}
            disabled={disabled}
          />
          I am a registered MSME
        </label>
        {values.is_msme ? (
          <div className="pf-grid" style={{ marginTop: 14 }}>
            <TextField
              {...common}
              name="msme_udyam_number"
              label="Udyam registration number"
              placeholder="UDYAM-MH-01-0001234"
              style={{ textTransform: 'uppercase' }}
            />
            <label className="iv-field">
              <span className="iv-label">Category</span>
              <select
                className={`iv-select${errorText(errors, 'msme_category') ? ' iv-input--invalid' : ''}`}
                value={values.msme_category || ''}
                onChange={(e) => onChange('msme_category', e.target.value)}
                disabled={disabled}
              >
                <option value="">Select…</option>
                <option value="micro">Micro</option>
                <option value="small">Small</option>
                <option value="medium">Medium</option>
              </select>
              {errorText(errors, 'msme_category') ? (
                <span className="iv-hint iv-hint--error">{errorText(errors, 'msme_category')}</span>
              ) : null}
            </label>
            {onFile ? (
              <label className="iv-field pf-full">
                <span className="iv-label">Udyam certificate</span>
                <input
                  className="pf-file"
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={(e) =>
                    onFile('msme_certificate', e.target.files && e.target.files[0])
                  }
                  disabled={disabled}
                />
                <span className="iv-hint">
                  {files && files.msme_certificate
                    ? files.msme_certificate.name
                    : 'Optional — PDF, PNG or JPG.'}
                </span>
              </label>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
